import { createFileRoute, Navigate } from '@tanstack/react-router';
import { AppLayout } from '@/components/layout/AppLayout';
import { useAuth } from '@/components/auth/AuthProvider';

/**
 * The admin shell's authentication gate (P4-4).
 *
 * This route previously imported `redirect` and never used it — there was no
 * `beforeLoad` and no check anywhere else in the chain. `__root` only PROVIDES
 * auth context and `AppLayout` never reads it, so every page under
 * `/_authenticated` rendered for an unauthenticated visitor.
 *
 * On its own that leaked no data, because every `/admin/*` API call 401s. But
 * combined with the mock fallbacks (P4-2) it was worse than a leak: the pages
 * answered those 401s with fabricated tenants and scheduler rows, under a green
 * "PROD" badge. Anyone who opened the URL saw a convincing production console
 * full of invented data — and so did a real operator whose token had expired.
 *
 * Guarded in the COMPONENT rather than `beforeLoad`, deliberately: auth lives in
 * React context (`AuthProvider` sits in `__root`), which `beforeLoad` cannot
 * read. Moving auth into the router context to satisfy the idiom would be a
 * larger change than the hole warrants, and this closes it completely — nothing
 * under the shell renders until `user` is present.
 *
 * `loading` must render neither the shell nor a redirect: Firebase resolves the
 * session asynchronously, so bouncing to /login while it is still `loading`
 * would sign out every operator on every refresh.
 */
function AuthenticatedLayout() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f6f6]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <AppLayout />;
}

export const Route = createFileRoute('/_authenticated')({
  component: AuthenticatedLayout,
});
